import { FormEvent, useEffect, useRef, useState } from 'react'
import { useSharedSurface } from './SharedSurfaceContext'

type PinDialogRequest = {
  message?: string
  okText?: string
  cancelText?: string
  onClose: (pin?: string) => void
}

type PinDialogSnapshot = PinDialogRequest & {
  id: number
}

const listeners = new Set<() => void>()
let currentDialog: PinDialogSnapshot | null = null
let nextDialogId = 1

function emitPinDialogChange() {
  for (const listener of listeners) listener()
}

export function showPinDialog(request: PinDialogRequest) {
  currentDialog = {
    ...request,
    id: nextDialogId++,
  }
  emitPinDialogChange()
}

function closePinDialog(pin?: string) {
  const dialog = currentDialog
  currentDialog = null
  emitPinDialogChange()
  dialog?.onClose(pin)
}

function subscribePinDialog(listener: () => void) {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

export function PinDialog() {
  const { skin } = useSharedSurface()
  const [dialog, setDialog] = useState(currentDialog)
  const [pin, setPin] = useState('')
  const inputRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => subscribePinDialog(() => setDialog(currentDialog)), [])

  useEffect(() => {
    setPin('')
    if (dialog) inputRef.current?.focus()
  }, [dialog?.id])

  if (!dialog) return null

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault()
    closePinDialog(pin)
  }

  return (
    <>
      <div className={`md-pin-dialog ${skin.pinOverlay}`} role="dialog" aria-modal="true" aria-label={dialog.message || 'Enter pin'}>
        <form className={`md-pin-dialog__box ${skin.pinBox}`} onSubmit={handleSubmit}>
          <div className={`md-pin-dialog__message ${skin.pinMessage}`}>{dialog.message || 'Please, enter edit pin'}</div>
          <input
            ref={inputRef}
            className={`md-pin-dialog__input ${skin.pinInput}`}
            type="password"
            value={pin}
            onChange={(event) => setPin(event.currentTarget.value)}
            autoComplete="current-password"
          />
          <div className={`md-pin-dialog__actions ${skin.pinActions}`}>
            <button className={`md-pin-dialog__button ${skin.pinButton}`} type="button" onClick={() => closePinDialog()}>
              {dialog.cancelText || 'Close'}
            </button>
            <button className={`md-pin-dialog__button md-pin-dialog__button--primary ${skin.pinButtonPrimary}`} type="submit">
              {dialog.okText || 'Ok'}
            </button>
          </div>
        </form>
      </div>
    </>
  )
}
